import type { ComponentType } from 'react';
import { useCallback, useRef } from 'react';

import type { DialogsContainerProps } from '@confluence/dialogs';
import { useDialogs } from '@confluence/dialogs/entry-points/useDialogs';
import { LoadableLazy } from '@confluence/loadable';

import type { DeleteModalProps } from './DeleteModal';

const createModalHook =
	<T extends ModalProps>(Modal: ComponentType<T>) =>
	(props1: Omit<T, keyof DialogsContainerProps | 'version'>) => {
		const { showModal } = useDialogs();

		const props1Ref = useRef<typeof props1>();
		props1Ref.current = props1;

		return useCallback(
			(props2: Pick<T, 'version'>) =>
				showModal(
					Modal,
					// @ts-ignore
					{ ...props1Ref.current!, ...props2 },
				),
			[showModal],
		);
	};

type ModalProps = DialogsContainerProps & Pick<DeleteModalProps, 'version'>;

const DeleteModal = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-content-history-modals" */ './DeleteModal'))
			.DeleteModal,
});

export const useDeleteModal = createModalHook(DeleteModal);

const RestoreModal = LoadableLazy({
	loader: async () =>
		(await import(/* webpackChunkName: "loadable-content-history-modals" */ './RestoreModal'))
			.RestoreModal,
});

export const useRestoreModal = createModalHook(RestoreModal);
